// use-notes.ts
// This file provides hooks for fetching and creating notes for a given case.
// Integrates with Athena/Cerner context and React Query for state management.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Note } from "@/types/notes";
import { useAppState } from "@/contexts/app-state";
import { getNotes, postNote } from "@/services/notes";

/**
 * useGetNotes Hook
 * Fetches notes for a given case ID, using the current user and department context.
 *
 * @param caseId - The unique identifier for the case
 * @returns The query object containing data, status, and helpers from React Query
 *
 * @example
 * const { data, isLoading } = useGetNotes("1234");
 */
export const useGetNotes = (caseId: string) => {
  const { loggedInUser, context } = useAppState();

  return useQuery<Note[], Error>({
    queryKey: ["notes", { user: loggedInUser, caseId }],
    queryFn: () =>
      getNotes({
        user: loggedInUser,
        practiceId: context?.athena?.practiceId || "8363",
        caseId,
      }),
  });
};

/**
 * useCreateNote Hook
 * Provides a mutation for creating a note for a given case ID.
 *
 * @param caseId - The unique identifier for the case
 * @returns An object with mutation state and a createNote function
 *
 * @example
 * const { createNote, isPending } = useCreateNote("1234");
 * createNote("This is a note");
 */
export const useCreateNote = (caseId: string) => {
  const queryClient = useQueryClient();
  const { loggedInUser, context } = useAppState();
  const { isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: postNote,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["notes", { user: loggedInUser, caseId }],
      });
    },
  });

  const createNote = (note: string) => {
    mutate({
      user: loggedInUser,
      practiceId: context?.athena?.practiceId || "8363",
      caseId,
      body: {
        actionNote: note,
        requestedUser: loggedInUser,
      },
    });
  };

  return {
    isPending,
    isError,
    error,
    isSuccess,
    createNote
  };
};

/**
 * useNotes Hook
 * Combines all notes-related hooks into a single interface for a given case ID.
 *
 * @param caseId - The unique identifier for the case
 * @returns An object with get and post helpers
 *
 * @example
 * const { get, post } = useNotes("1234");
 * const { data, isLoading } = get;
 * // or
 * const { get: { data, isLoading }, post: { createNote } } = useNotes("1234");
 */
export const useNotes = (caseId: string) => {
  return {
    get: useGetNotes(caseId),
    post: useCreateNote(caseId)
  };
};
